import moment from 'moment'
import {
  groupWith,
  isEmpty 
} from 'ramda'
import React,
{ Component } from 'react'

import { Button } from '@driftt/dds'
import { faPenToSquare } from '@driftt/dds.icons/dist/light/faPenToSquare'
import {
  Div,
  Footer,
  H2,
  H3,
  Modal,
  Span,
  TextArea 
} from '@driftt/tide-core'
import { Select } from '@driftt/tide-selects'
import { tracking } from '@driftt/traffic-control'
import {
  getContactTimeline,
  postNewContactNote 
} from 'api/contacts'
import { resolveContactName } from 'utils/contact'

import TimelineEntries from './TimelineEntries'

import './style.css'

const TimelineFilters = [{
  label: 'All events',
  value: 'NONE' 
} ,{
  label: 'Notes',
  value: 'NOTE' 
}, {
  label: 'Conversations',
  value: 'CONVERSATION' 
}, {
  label: 'Campaigns',
  value: 'CAMPAIGN' 
}, {
  label: 'Property',
  value: 'PROPERTY' 
}, {
  label: 'Triggered Events',
  value: 'EVENT' 
}, {
  label: 'Meetings Booked',
  value: 'BOOKED_MEETING' 
}, {
  label: 'Calls',
  value: 'VOICE_CALL' 
}]

const DEFAULT_EVENTS_SHOWN = 10
const LOAD_MORE_NUMBER_OF_NEW_EVENTS = 10

const TIMELINE_EVENTS = {
  'TAG_ADD': TimelineEntries.TagEvent,
  'TAG_REMOVE': TimelineEntries.TagEvent,
  'TRIGGERED_EVENT': TimelineEntries.TriggeredEvent,
  'PAGE': TimelineEntries.PageEvent,
  'ADDED': TimelineEntries.AddedEvent,
  'CONVERSATION': TimelineEntries.ConvoEvent,
  'NOTE': TimelineEntries.NoteEvent,
  'BOOKED_MEETING': TimelineEntries.MeetingBookedEvent,
  'VOICE_CALL': TimelineEntries.VoiceCallEvent
}

class Timeline extends Component {
  constructor(props) {
    super(props)

    this.state = {
      timeline: [],
      firstLoad: true,
      isLoadingTimeline: false,
      showAddNoteModal: false,
      currentFilter: TimelineFilters[0],
      pagingSize: DEFAULT_EVENTS_SHOWN
    }
  }

  componentDidMount() {
    this.getTimeline()
    this.resolveCurrentFilter()

    const { contact } = this.props

    tracking.trackEvent('Contact Timeline - Timeline Viewed', { endUserId: contact.id })
  }

  getTimeline = async () => {
    const { contact } = this.props
    const {
      currentFilter = {},
      pagingSize,
      startMillis 
    } = this.state

    this.setState({ isLoadingTimeline: true })

    try {
      const { data: timeline } = await getContactTimeline({
        contactId: contact.id,
        filter: (currentFilter.value === 'NONE' ? null : currentFilter.value),
        size: pagingSize,
        startMillis: startMillis,
        resolveMaster: false
      })

      this.setState(prevState => ({
        timeline:[...prevState.timeline, ...timeline],
        isLoadingTimeline: false,
      }), this.prepareData)
    } catch(e) {
      console.warn(e)
    }
  }

  resolveCurrentFilter = () => {
    const { currentFilter } = this.state

    if (!currentFilter) {
      return
    }

    this.setState(() => ({ currentFilter: TimelineFilters.filter(({ value }) => value === currentFilter.value) }))
  }

  onFilterTimeline = (option) => {
    this.setState({
      currentFilter: option,
      pagingSize: DEFAULT_EVENTS_SHOWN,
      timeline: [],
      startMillis: null
    }, this.getTimeline)
  }

  addNote = () => {
    this.setState(prevState => ({ showAddNoteModal: !prevState.showAddNoteModal }))
  }

  onNoteEdit = (value) => {
    this.setState({ noteText: value })
  }

  saveNote = async () => {
    const { contact } = this.props
    const { noteText } = this.state

    tracking.trackEvent('Contact Timeline - Note Saved', { endUserId: contact.id })

    try {
      await postNewContactNote(contact.id, noteText)

      this.getTimeline()
      this.addNote()
    } catch(e) {
      console.warn(e)
    }
  }

  prepareData = () => {
    const { timeline } = this.state

    const filterDateAndMonth = (a, b) => {
      const firstEvent = moment(a.createdAt)
      const secondEvent = moment(b.createdAt)
      return firstEvent.month() === secondEvent.month() && firstEvent.year() === secondEvent.year()
    }

    const groupedByMonthAndYear = groupWith(filterDateAndMonth, timeline)

    this.setState({
      groupedTimeline: groupedByMonthAndYear,
      firstLoad: false
    })
  }

  determineDisplay = ({ entry }) => {
    const { contact } = this.props
    let DisplayEvent

    if (entry.eventType === 'TRIGGERED_EVENT' && entry.body.event === 'page') {
      DisplayEvent = TIMELINE_EVENTS.PAGE
    } else {
      DisplayEvent = TIMELINE_EVENTS[entry.eventType]
    }

    return (
      <DisplayEvent
        event={entry}
        name={resolveContactName(contact)}
        key={`${entry.eventType}-${entry.endUserId}-${entry.createdAt}`}
      />
    )
  }

  loadMoreTimelineEntries = () => {
    this.setState(state => ({
      pagingSize: LOAD_MORE_NUMBER_OF_NEW_EVENTS,
      startMillis: state.timeline[state.timeline.length - 1].createdAt
    }), this.getTimeline)
  }

  renderTimeline = () => {
    const {
      groupedTimeline,
      isLoadingTimeline,
      timeline,
      pagingSize 
    } = this.state

    if (isEmpty(groupedTimeline)) {
      return (
        <Div className="contact-details--no-entries">
          <H2>Sorry, nothing matched your criteria for this contact.</H2>
          <H3>Please select another.</H3>
        </Div>
      )
    }

    const events = groupedTimeline.map(month => {
      return (
        <Div
          className="contact-details--month-container"
          key={`contact-timeline-month-${month[0].createdAt}`}
          data-private
        >
          <Div className="contact-details--month-banner">
            {`HISTORY FOR`}
            <Span className="contact-details--month-banner--accent">
              {moment(month[0].createdAt).format('MMMM YYYY')}
            </Span>
          </Div>
          {
            month.map(entry => this.determineDisplay({ entry }))
          }
        </Div>
      )
    })

    const hasNotLoadedEverything = pagingSize <= timeline.length || isLoadingTimeline

    const loadMoreArea = (
      <Div className="contact-details--timeline--load-more-button">
        {hasNotLoadedEverything && (
          <Button
            loading={isLoadingTimeline}
            onClick={this.loadMoreTimelineEntries}
            variant="secondary"
          >
              Load more
          </Button>
        )
        }
      </Div>
    )

    return (
      <Div>
        {events}
        {loadMoreArea}
      </Div>
    )
  }

  render() {
    const {
      firstLoad,
      showAddNoteModal,
      noteText,
      currentFilter 
    } = this.state

    return (
      <Div className="contact-details--attributes-container contact-details--attributes-container--timeline">
        <Div className="contact-details--timeline-header">
          <Button variant='tertiary'
            onClick={this.addNote}>
            <Button.Icon icon={faPenToSquare}/>
            Add note to activity timeline
          </Button>
          <Div className="contact-details--filter-timeline">
            <Div>
              <Span>Filter activity timeline by:</Span>
            </Div>
            <Select
              options={TimelineFilters}
              value={currentFilter}
              onChange={this.onFilterTimeline}
              clearable={false}
              searchable={false}
            />
          </Div>
        </Div>
        {!firstLoad &&
          this.renderTimeline()
        }
        <Modal
          open={showAddNoteModal}
          title="Add a note"
          size="small"
          onClose={this.addNote}
          className="contact-details--add-note-modal"
        >
          <TextArea value={noteText} onChange={e => this.onNoteEdit(e.target.value)} />
          <Footer>
            <Button onClick={this.saveNote}>Save note</Button>
          </Footer>
        </Modal>
      </Div>
    )
  }
}

export default Timeline
